<script setup lang="ts">
    import { ref, onMounted } from 'vue'
    import {
        useLightbox,
        useImageSizes,
        type LightboxImage,
    } from './composables/useLightbox'

    const props = withDefaults(
        defineProps<{
            images: LightboxImage[]
            /** Index of the image to open first when using `open()` */
            startIndex?: number
            loop?: boolean
            downloadable?: boolean
            openInNewTab?: boolean
        }>(),
        {
            startIndex: 0,
            loop: true,
            downloadable: true,
            openInNewTab: true,
        },
    )

    const galleryRef = ref<HTMLElement>()
    const { preload, getSize } = useImageSizes()

    const { lightbox, init } = useLightbox({
        gallery: galleryRef,
        loop: props.loop,
        downloadable: props.downloadable,
        openInNewTab: props.openInNewTab,
    })

    /**
     * Opens the lightbox at the given index (defaults to `startIndex` prop).
     */
    const open = (index?: number) => {
        if (!lightbox.value) {
            return
        }
        lightbox.value.loadAndOpen(index ?? props.startIndex)
    }

    const captionFor = (image: LightboxImage) =>
        image.caption ?? image.alt ?? ''

    onMounted(async () => {
        preload(props.images)
        await init()
    })

    defineExpose({ open })
</script>

<template>
    <!-- Hidden gallery container — PhotoSwipe discovers items via DOM queries -->
    <div ref="galleryRef" style="display: contents">
        <a
            v-for="image in images"
            :key="image.url"
            :href="image.url"
            :data-pswp-src="image.url"
            :data-pswp-width="getSize(image.url).width"
            :data-pswp-height="getSize(image.url).height"
            :data-pswp-caption="captionFor(image)"
            hidden
            target="_blank"
            rel="noopener noreferrer" />
        <!-- Default slot receives `open` so consumers control what triggers the lightbox -->
        <slot :open="open" />
    </div>
</template>
